import { importProvidersFrom, NgModule } from '@angular/core';
import { RouterModule, Routes, UrlSegment } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';

import { AUTH_ROUTES } from '@ninety/platform/models/constants/auth-routes';
import { Scopes } from '@ninety/platform/models/entitlements/entitlements-model';
import { ItemType } from '@ninety/platform/models/enums/item-type';
import { ActiveSubscriptionGuard } from '@ninety/ui/legacy/core/guards/active-subscription.guard';
import {
  ACCESS_SUSPENDED_PATH,
  companyAccessSuspendedGuard,
} from '@ninety/ui/legacy/core/guards/company-access-suspended.guard';
import { companyMfaSetupGuard } from '@ninety/ui/legacy/core/guards/company-mfa-setup.guard';
import { hasFeatureAccessGuard } from '@ninety/ui/legacy/core/guards/has-feature-access.guard';
import { HelpfulPermissionsGuard } from '@ninety/ui/legacy/core/guards/helpful-permissions.guard';
import { MasteryGuard } from '@ninety/ui/legacy/core/guards/mastery.guard';
import { PartnerHubAccessGuard } from '@ninety/ui/legacy/core/guards/partner-hub-access.guard';
import { PartnersPermissionsGuard } from '@ninety/ui/legacy/core/guards/partners-permissions.guard';
import { PrivateAccountabilityChartGuard } from '@ninety/ui/legacy/core/guards/private-accountability-chart.guard';
import { SwitchCompanyGuard } from '@ninety/ui/legacy/core/guards/switch-company.guard';
import { AuthGuard, hasAccessTokenGuard, hasTier1DataGuard, hasTier2DataGuard } from '@ninety/ui/legacy/core/index';
import { environment } from '@ninety/ui/web/environments';
import { canTakeAssessmentGuard } from '@ninety/web/pages/assessments/_guards/can-take-assessment.guard';
import { onExitSurvey } from '@ninety/web/pages/assessments/_guards/on-exit-survey.guard';
import { COMMERCIAL_MODEL_ROUTES } from '@ninety/web/pages/commercial-model/commercial-model.routes';
import { DetailViewEffects } from '@ninety/web/pages/detail-view/_state/detail-view.effects';
import { HeadlineResolver } from '@ninety/web/pages/headlines/_shared/resolvers/headline.resolver';
import { inboxFlagGuard } from '@ninety/web/pages/inbox/guards/inbox-flag.guard';
import { AccessSuspendedComponent } from '@ninety/web/pages/layouts/components/access-suspended/access-suspended.component';
import { BecomeAPartnerComponent } from '@ninety/web/pages/layouts/components/become-a-partner/become-a-partner.component';
import { HelpfulComponent } from '@ninety/web/pages/layouts/components/helpful/helpful.component';
import { HomeComponent } from '@ninety/web/pages/layouts/components/home/home.component';
import { IntegrationConsentComponent } from '@ninety/web/pages/layouts/components/integration-consent/integration-consent.component';
import { OauthPopupCallbackComponent } from '@ninety/web/pages/layouts/components/oauth-popup-callback/oauth-popup-callback.component';
import { LearningModuleGuard } from '@ninety/web/pages/learning-modules/learning-module.guard';
import { TroubleGettingCodeComponent } from '@ninety/web/pages/login/_shared/mfa/trouble-getting-code.component';
import { TotpSetupPageComponent } from '@ninety/web/pages/login/login/totp-setup-page.component';
import { ReferralComponent } from '@ninety/web/pages/referral/referral.component';
import { RocksStateEffects } from '@ninety/web/pages/rocks/_state/rocks-state.effects';

export const TEST_ROUTES: Routes = [
  {
    // DEV-25314 — Rich Text Editor POC: Decoupled Editor Shell (Story 1b)
    // Gated by environment.enableTestRoutes — enabled in local, dev, and reverseProxy;
    // explicitly disabled in QA, staging, and prod.
    // Navigate to /rte-demo to exercise the decoupled image-upload demo.
    path: 'rte-demo',
    loadComponent: () =>
      import('@ninety/ui/legacy/components/ninety-rich-text-editor/ninety-rich-text-editor-demo.component').then(
        m => m.NinetyRichTextEditorDemoComponent
      ),
    data: { title: 'Rich Text Editor — Decoupled Shell Demo' },
  },
];

const companyMatcher = (url: UrlSegment[]) => {
  if (url.length && url[0].path === 'company') {
    return { consumed: url };
  }
  return null;
};

const routes: Routes = [
  ...(environment.enableTestRoutes ? TEST_ROUTES : []),
  {
    matcher: companyMatcher,
    canActivate: [hasAccessTokenGuard, SwitchCompanyGuard],
    children: [],
  },
  // Auth routes - these don't require authentication.
  {
    path: AUTH_ROUTES.login,
    loadChildren: () => import('@ninety/web/pages/login/login.routes').then(m => m.loginRoutes),
  },
  {
    path: AUTH_ROUTES.totp,
    redirectTo: `${AUTH_ROUTES.login}/totp`,
    pathMatch: 'full',
  },
  {
    path: AUTH_ROUTES.setupTotp,
    component: TotpSetupPageComponent,
    title: 'TOTP Setup',
  },
  {
    path: AUTH_ROUTES.troubleGettingCode,
    component: TroubleGettingCodeComponent,
    title: 'Trouble Getting Code',
  },
  { path: AUTH_ROUTES.signUp, redirectTo: `${AUTH_ROUTES.login}/sign-up`, pathMatch: 'full' },
  {
    path: AUTH_ROUTES.partnerSignUp,
    redirectTo: `${AUTH_ROUTES.login}/partner-sign-up`,
    pathMatch: 'full',
  },
  // Vanity short-link. Not in AUTH_ROUTES on purpose:
  // bootstrap.effects.ts substring-matches AUTH_ROUTES values, and a bare
  // 'partner' would collide with /partner-hub, /partners, /become-a-partner.
  { path: 'partner', redirectTo: `${AUTH_ROUTES.login}/partner-sign-up`, pathMatch: 'full' },
  { path: AUTH_ROUTES.acceptInvite, redirectTo: `${AUTH_ROUTES.login}/accept-invite`, pathMatch: 'full' },
  { path: AUTH_ROUTES.registration, redirectTo: `${AUTH_ROUTES.login}/sign-up`, pathMatch: 'full' },
  {
    path: 'create-company',
    loadComponent: () =>
      import('@ninety/web/pages/create-company/create-company.component').then(c => c.CreateCompanyComponent),
    data: { title: 'Create Company' },
    canActivate: [],
  },
  {
    path: 'survey/:userAssessmentId',
    redirectTo: 'assessment/:userAssessmentId',
  },
  {
    path: 'assessment/:userAssessmentId',
    loadComponent: () =>
      import('@ninety/web/pages/assessments/taker/assessment/assessment.component').then(c => c.AssessmentComponent),
    canActivate: [hasAccessTokenGuard, hasTier1DataGuard, companyAccessSuspendedGuard, canTakeAssessmentGuard],
    canDeactivate: [onExitSurvey],
  },
  {
    path: 'report-viewer/:reportName/:reportId/:reportAction',
    loadComponent: () =>
      import('@ninety/web/pages/report-viewer/report-viewer.component').then(c => c.ReportViewerComponent),
    canActivate: [hasAccessTokenGuard],
  },
  {
    path: 'consent',
    component: IntegrationConsentComponent,
    canActivate: [hasAccessTokenGuard, AuthGuard, hasTier1DataGuard, companyAccessSuspendedGuard],
  },
  // Landing page for the popup-window OAuth connect flow (see NativeIntegrationService.
  // connectCalendarViaPopup) - no guards, since it only relays query params back to its
  // opener via postMessage and never makes an HTTP call itself.
  {
    path: 'oauth-popup-callback',
    component: OauthPopupCallbackComponent,
  },
  {
    path: ACCESS_SUSPENDED_PATH,
    component: AccessSuspendedComponent,
    canActivate: [hasAccessTokenGuard, AuthGuard, hasTier1DataGuard],
  },
  {
    path: '',
    component: HomeComponent,
    canActivate: [hasAccessTokenGuard, AuthGuard, hasTier1DataGuard, companyAccessSuspendedGuard, companyMfaSetupGuard],
    canActivateChild: [hasTier1DataGuard, hasTier2DataGuard],
    children: [
      {
        path: 'home',
        loadChildren: () => import('@ninety/web/pages/my-ninety/my-ninety-routes').then(m => m.MyNinetyRoutes),
        canActivate: [hasAccessTokenGuard, AuthGuard, hasFeatureAccessGuard(Scopes.my90)],
      },
      {
        path: 'inbox',
        loadChildren: () => import('@ninety/web/pages/inbox/inbox.routes').then(m => m.inboxRoutes),
        canMatch: [inboxFlagGuard],
        canActivate: [hasAccessTokenGuard, AuthGuard, ActiveSubscriptionGuard],
      },
      {
        path: 'data',
        loadChildren: () => import('@ninety/web/pages/scorecard/routes').then(m => m.ScorecardRoutes),
        canActivate: [
          hasAccessTokenGuard,
          AuthGuard,
          ActiveSubscriptionGuard,
          hasFeatureAccessGuard(Scopes.scorecard),
          hasTier2DataGuard,
        ],
      },
      // Redirect legacy data-v2 routes to new data routes
      {
        path: 'data-v2',
        redirectTo: 'data/scorecards/weekly',
      },
      {
        path: 'kpi-manager',
        loadChildren: () => import('@ninety/web/pages/kpi-manager/routes').then(m => m.KPI_MANAGER_ROUTES),
        canActivate: [hasAccessTokenGuard, AuthGuard, ActiveSubscriptionGuard],
        data: {
          checkMinRole: 'admin',
        },
        // TODO: Need to address custom language in title
      },
      {
        path: 'rocks',
        loadChildren: () => import('@ninety/web/pages/rocks/rocks.routes').then(m => m.RocksRoutes),
        canActivate: [hasAccessTokenGuard, AuthGuard, ActiveSubscriptionGuard, hasFeatureAccessGuard(Scopes.rocks)],
      },
      {
        path: 'milestones/:id',
        loadComponent: () =>
          import('@ninety/web/pages/rocks/milestone/milestone.component').then(c => c.MilestoneComponent),
        canActivate: [hasAccessTokenGuard, AuthGuard, ActiveSubscriptionGuard],
        providers: [
          importProvidersFrom(EffectsModule.forFeature([DetailViewEffects])),
          importProvidersFrom(EffectsModule.forFeature([RocksStateEffects])),
        ],
      },
      {
        path: 'todos',
        loadChildren: () => import('@ninety/web/pages/todos/todos.routes').then(m => m.TodosRoutes),
        canActivate: [hasAccessTokenGuard, AuthGuard, ActiveSubscriptionGuard, hasFeatureAccessGuard(Scopes.todos)],
      },
      {
        path: 'issues',
        loadChildren: () => import('@ninety/web/pages/issues-v2/issues-v2.routes').then(m => m.issuesV2Routes),
        canActivate: [
          hasAccessTokenGuard,
          AuthGuard,
          ActiveSubscriptionGuard,
          hasFeatureAccessGuard(Scopes.issues),
          hasTier2DataGuard,
        ],
      },
      {
        path: 'vision',
        loadChildren: () => import('@ninety/web/pages/vto/vto.routes').then(m => m.vtoRoutes),
        canActivate: [hasAccessTokenGuard, AuthGuard, ActiveSubscriptionGuard, hasFeatureAccessGuard(Scopes.vision)],
      },
      {
        path: 'meeting/:meetingId',
        loadChildren: () => import('@ninety/web/pages/meeting/meeting.routes').then(m => m.meetingRoutes),
        canActivate: [hasAccessTokenGuard, AuthGuard, hasFeatureAccessGuard(Scopes.weeklyMeetings)],
      },
      {
        path: 'meetings',
        loadChildren: () => import('@ninety/web/pages/meeting/meeting.routes').then(m => m.meetingRoutes),
        canActivate: [
          hasAccessTokenGuard,
          AuthGuard,
          ActiveSubscriptionGuard,
          hasFeatureAccessGuard(Scopes.weeklyMeetings),
          hasTier2DataGuard,
        ],
      },
      {
        path: 'headlines',
        loadComponent: () => import('@ninety/web/pages/headlines/headlines.component').then(c => c.HeadlinesComponent),
        data: { itemType: ItemType.headline },
        canActivate: [
          hasAccessTokenGuard,
          AuthGuard,
          ActiveSubscriptionGuard,
          hasFeatureAccessGuard(Scopes.headlines),
          hasTier2DataGuard,
        ],
      },
      {
        path: 'headlines/:id',
        loadComponent: () =>
          import('@ninety/web/pages/headlines/_shared/headline-card/headline-card.component').then(
            c => c.HeadlineCardComponent
          ),
        canActivate: [hasAccessTokenGuard, AuthGuard, ActiveSubscriptionGuard, hasFeatureAccessGuard(Scopes.headlines)],
        resolve: { headline: HeadlineResolver },
      },
      {
        path: 'chart',
        loadChildren: () =>
          import('@ninety/web/pages/accountability-chart/routes/org-chart-routing.factory').then(r =>
            r.createOrgChartChildRoutes({ withStateModule: true })
          ),
        canActivate: [
          hasAccessTokenGuard,
          AuthGuard,
          ActiveSubscriptionGuard,
          PrivateAccountabilityChartGuard,
          hasTier2DataGuard,
        ],
      },
      {
        path: 'insights',
        canActivate: [
          hasAccessTokenGuard,
          AuthGuard,
          ActiveSubscriptionGuard,
          hasFeatureAccessGuard(Scopes.insights),
          hasTier2DataGuard,
        ],
        loadChildren: () => import('@ninety/web/pages/insights/insights.routes').then(m => m.insightsRoutes),
      },
      {
        path: '1-on-1',
        loadChildren: () => import('@ninety/web/pages/feedback/feedback.routes').then(m => m.feedbackRoutes),
        canActivate: [hasAccessTokenGuard, AuthGuard, hasFeatureAccessGuard(Scopes.oneOnOne), hasTier2DataGuard],
      },
      { path: 'conversation/:id/prepare-form', redirectTo: '/1-on-1/discussion/:id/form', pathMatch: 'full' },
      { path: 'feedback/quarterly', redirectTo: '/1-on-1/discussions/quarterly', pathMatch: 'full' },
      { path: 'feedback/annual', redirectTo: '/1-on-1/discussions/annual', pathMatch: 'full' },
      { path: 'feedback/fit-check', redirectTo: '/1-on-1/discussions/fit-check', pathMatch: 'full' },
      { path: 'feedback/people-analyzer', redirectTo: '/1-on-1/discussions/people-analyzer', pathMatch: 'full' },
      { path: 'feedback/fit-check-v2', redirectTo: '/1-on-1/discussions/fit-check-v2', pathMatch: 'full' },
      { path: 'feedback', redirectTo: '/1-on-1/discussions/quarterly', pathMatch: 'full' },
      {
        path: 'directory',
        loadChildren: () => import('@ninety/web/pages/directory/directory.routes').then(m => m.directoryRoutes),
        canActivate: [hasAccessTokenGuard, AuthGuard, hasFeatureAccessGuard(Scopes.directory)],
      },
      {
        path: 'mastery',
        loadChildren: () => import('@ninety/web/pages/mastery/mastery.routes').then(m => m.masteryRoutes),
        canActivate: [
          hasAccessTokenGuard,
          AuthGuard,
          ActiveSubscriptionGuard,
          MasteryGuard,
          hasFeatureAccessGuard(Scopes.mastery),
        ],
      },
      {
        path: 'knowledge',
        data: { learningModulesSource: 'learning-modules' },
        loadChildren: () =>
          import('@ninety/web/pages/learning-modules/learning-modules.routes').then(m => m.learningModuleRoutes),
        canActivate: [
          hasAccessTokenGuard,
          AuthGuard,
          LearningModuleGuard,
          hasFeatureAccessGuard(Scopes.kst),
          hasTier2DataGuard,
        ],
      },
      {
        path: 'surveys',
        redirectTo: 'assessments',
      },
      {
        path: 'assessments',
        loadChildren: () =>
          import('@ninety/web/pages/assessments/assessments-routing.module').then(m => m.SurveyRoutes),
        canActivate: [
          hasAccessTokenGuard,
          AuthGuard,
          ActiveSubscriptionGuard,
          hasFeatureAccessGuard(Scopes.assessments),
        ],
      },
      {
        path: 'settings',
        loadChildren: () => import('@ninety/web/pages/settings/settings.routes').then(m => m.settingsRoutes),
        canActivate: [AuthGuard],
      },
      {
        path: 'partner-hub',
        loadChildren: () =>
          import('@ninety/web/pages/partners/partner-hub/partner-hub.routes').then(m => m.partnerHubRoutes),
        canActivate: [hasAccessTokenGuard, AuthGuard, PartnerHubAccessGuard],
      },
      {
        path: 'helpful',
        component: HelpfulComponent,
        canActivate: [hasAccessTokenGuard, AuthGuard, HelpfulPermissionsGuard.hasAnyHelpfulPermission],
        data: { title: 'Helpful' },
      },
      {
        path: 'become-a-partner',
        component: BecomeAPartnerComponent,
        canActivate: [hasAccessTokenGuard, AuthGuard, ActiveSubscriptionGuard],
        data: { title: 'Become a Partner!' },
      },
      {
        path: 'partners',
        loadChildren: () =>
          import('@ninety/web/pages/partners/partner-management/partners.routes').then(m => m.partnerRoutes),
        canActivate: [hasAccessTokenGuard, AuthGuard, PartnersPermissionsGuard],
        title: 'Partners',
      },
      {
        path: 'referral',
        component: ReferralComponent,
        canActivate: [AuthGuard],
        data: { title: 'Referral' },
      },
      {
        path: 'detail',
        outlet: 'detail',
        loadChildren: () => import('@ninety/web/pages/detail-view/detail-view.routes').then(m => m.detailViewRoutes),
      },

      /** Commercial Model Empty States */
      ...COMMERCIAL_MODEL_ROUTES,

      // for preserving external links
      { path: 'accountability-chart', redirectTo: '/chart', pathMatch: 'full' },
      { path: 'vto', redirectTo: '/vision/vision', pathMatch: 'full' },
      { path: 'dashboard', redirectTo: 'insights', pathMatch: 'full' },
      { path: 'insights', redirectTo: '/insights', pathMatch: 'full' },
      { path: '', redirectTo: '/home', pathMatch: 'full' },
    ],
  },

  // when releasing to prod, you need to add a redirect here
  { path: 'v4/my-90', redirectTo: '/home' },
  { path: 'v4/my-focus', redirectTo: '/home' },
  { path: 'v4/data', redirectTo: '/data/scorecards/weekly' },
  { path: 'v4/rocks', redirectTo: '/rocks' },
  { path: 'v4/todos', redirectTo: '/todos' },
  { path: 'v4/issues', redirectTo: '/issues/short-term' },
  { path: 'v4/meetings', redirectTo: '/meetings' },
  { path: 'v4/vto', redirectTo: '/vto' },
  { path: 'v4/feedback', redirectTo: '/1-on-1/discussions/quarterly' },
  { path: 'v4/directory', redirectTo: '/directory' },
  { path: 'v4/accountability-chart', redirectTo: '/accountability-chart' },
  { path: 'v4/login', redirectTo: 'login' },
  { path: 'v4/settings', redirectTo: '/settings' },
  { path: 'billing', redirectTo: '/settings/billing/overview', pathMatch: 'full' },
  { path: '**', redirectTo: '' },
];
@NgModule({
  imports: [
    RouterModule.forRoot(routes /*routeConfig*/, {
      scrollPositionRestoration: 'enabled', // Emits Scroll event from the router after navigation, allowing re-attached components to restore position.
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
